import env from '@trashlab/sitesapp/src/env';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Flex, Button } from '@radix-ui/themes';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  Marker,
} from '@vis.gl/react-google-maps';
import {
  IconArrowsMove,
  IconRotateClockwise2,
  IconRestore,
} from '@tabler/icons-react';
import { useIsMobile } from '@trashlab/sitesapp/src/organizations/piedmont/hooks/use-mobile';
import { AVERAGE_DUMPSTER_DIMENSIONS } from '@trashlab/api/src/domains/container';

const HIGH_CONTRAST_ICON_COLOR = '#ECE81A';
const HIGH_CONTRAST_ICON_BORDER_COLOR = '#0066FF';

const DumpsterMarker = ({
  initialRotation = 0,
  onRotationChange,
  isMobile,
}: {
  initialRotation?: number;
  onRotationChange?: (rotation: number) => void;
  isMobile: boolean;
}) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map?.getZoom() ?? 21);

  const baseSize = AVERAGE_DUMPSTER_DIMENSIONS.length;
  const zoomFactor = Math.pow(2, zoom - 21);
  const iconSize = baseSize * zoomFactor * 4;

  const [rotation, setRotation] = useState(initialRotation);

  useEffect(() => {
    setRotation(initialRotation);
  }, [initialRotation]);

  const [isDragging, setIsDragging] = useState(false);
  const [startAngle, setStartAngle] = useState(0);
  const [startRotation, setStartRotation] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleRotate = (direction: 'clockwise' | 'counterclockwise') => {
    const delta = direction === 'clockwise' ? 15 : -15;
    const newRotation = (((rotation + delta) % 360) + 360) % 360;
    setRotation(newRotation);
    onRotationChange?.(newRotation);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!containerRef.current) return;

    setIsDragging(true);
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const angle =
      Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    setStartAngle(angle);
    setStartRotation(rotation);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const currentAngle =
      Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);

    let deltaAngle = currentAngle - startAngle;

    if (deltaAngle > 180) deltaAngle -= 360;
    if (deltaAngle < -180) deltaAngle += 360;

    let newRotation = startRotation + deltaAngle;
    newRotation = ((newRotation % 360) + 360) % 360;

    const snappedRotation = Math.round(newRotation / 15) * 15;

    setRotation(snappedRotation);
    onRotationChange?.(snappedRotation);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, startAngle, startRotation]);

  useEffect(() => {
    const listener = map?.addListener('zoom_changed', () => {
      setZoom(map.getZoom() ?? 21);
    });
    return () => {
      listener?.remove();
    };
  }, [map]);

  return (
    <Box
      ref={containerRef}
      style={{
        position: 'relative',
        width: iconSize,
        height: iconSize * 0.4,
        backgroundColor: HIGH_CONTRAST_ICON_COLOR,
        border: `2px solid ${HIGH_CONTRAST_ICON_BORDER_COLOR}`,
        pointerEvents: 'all',
        transform: `rotate(${rotation}deg)`,
        transition: isDragging ? 'none' : 'transform 0.3s ease',
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconArrowsMove
          color={HIGH_CONTRAST_ICON_BORDER_COLOR}
          size={iconSize / 3}
        />
      </Box>
      {!isMobile && (
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            right: -30,
            transform: 'translateY(-50%)',
            cursor: 'grab',
            backgroundColor: '#fff',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #fff',
          }}
          onMouseDown={handleMouseDown}
        >
          <IconRotateClockwise2 size={16} color="#1A1B1E" />
        </Box>
      )}
    </Box>
  );
};

const GMapsMap = ({
  position,
  onChange,
  placeId,
  initialRotation = 0,
  onRotationChange,
  onReset,
}: {
  position: { lat: number; lng: number };
  onChange: (position: { lat: number; lng: number }) => void;
  placeId: string;
  initialRotation?: number;
  onRotationChange?: (rotation: number) => void;
  onReset?: () => void;
}) => {
  const map = useMap();
  const isMobile = useIsMobile();

  const [currentRotation, setCurrentRotation] = useState(initialRotation);

  const [storedPlaceId, setPlaceId] = useState(placeId);
  const initialPositionRef = useRef(position);

  useEffect(() => {
    if (placeId !== storedPlaceId) {
      map?.setCenter(position);
      setPlaceId(placeId);
    }
  }, [placeId, storedPlaceId]);

  // Smaller map on mobile devices for better UX
  return (
    <div className="relative w-full h-[12rem] sm:h-[15rem] my-2">
      <Map
        defaultCenter={position}
        mapTypeId={'hybrid'}
        mapId={`${position}`}
        defaultTilt={0}
        rotateControl={true}
        tiltInteractionEnabled={false}
        defaultZoom={21}
        zoomControl={true}
        fullscreenControl={true}
        maxZoom={21}
        minZoom={20}
        disableDefaultUI={true}
        scrollwheel={false}
        onCenterChanged={(map) => {
          onChange(map.detail.center);
        }}
      >
        <AdvancedMarker position={position}>
          <DumpsterMarker
            initialRotation={currentRotation}
            onRotationChange={(rotation) => {
              setCurrentRotation(rotation);
              onRotationChange?.(rotation);
            }}
            isMobile={isMobile}
          />
        </AdvancedMarker>
      </Map>
      <Box
        style={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          whiteSpace: 'nowrap',
          zIndex: 10,
        }}
      >
        Drag and drop dumpster for placement location
      </Box>
      <Flex
        gap="2"
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 10,
        }}
      >
        <Button
          type="button"
          variant="soft"
          style={{
            borderRadius: '4px',
            backgroundColor: '#fff',
            color: '#1A1B1E',
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Only reset the dumpster marker position and rotation, not the address
            onChange(initialPositionRef.current);
            map?.setCenter(initialPositionRef.current);
            setCurrentRotation(0);
            onRotationChange?.(0);
          }}
        >
          <IconRestore size={20} />
          Reset to initial position
        </Button>
        {isMobile && (
          <Button
            type="button"
            variant="soft"
            style={{
              borderRadius: '4px',
              backgroundColor: '#fff',
              color: '#1A1B1E',
              display: 'flex',
              gap: '4px',
              alignItems: 'center',
            }}
            onClick={(e) => {
              e.preventDefault();
              const newRotation = (((currentRotation + 15) % 360) + 360) % 360;
              setCurrentRotation(newRotation);
              onRotationChange?.(newRotation);
            }}
          >
            <IconRotateClockwise2 size={20} />
            Rotate
          </Button>
        )}
      </Flex>
    </div>
  );
};

export const GMapsPinDropper = ({
  placeId,
  position,
  onChange,
  initialRotation = 0,
  onRotationChange,
  onReset,
}: {
  placeId: string;
  position: { lat: number; lng: number };
  onChange: (position: { lat: number; lng: number }) => void;
  initialRotation?: number;
  onRotationChange?: (rotation: number) => void;
  onReset?: () => void;
}) => {
  return (
    <APIProvider
      apiKey={env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? ''}
      libraries={['places', 'marker', 'geometry']}
    >
      <GMapsMap
        position={position}
        onChange={onChange}
        placeId={placeId}
        initialRotation={initialRotation}
        onRotationChange={onRotationChange}
        onReset={onReset}
      />
    </APIProvider>
  );
};
